import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Globe, Loader2, AlertTriangle, CheckCircle2, Wand2, Undo2 } from "lucide-react";
import { http, errMsg } from "../../lib/api";

const CLOSED_RE = /permanently|has closed|is closed|site is closed|door closed|no longer (open|operat|trad)|ceased|will close|closing down|shut down/i;
const isClosedFlag = (f) => f.closed || CLOSED_RE.test(f.current || "");

const Flag = ({ kitId, n, i, f, reload }) => {
  const [busy, setBusy] = useState(false);
  const a = f.applied;
  const closed = isClosedFlag(f);
  const call = async (path, msg) => {
    setBusy(true);
    try { const { data } = await http.post(`/kits/${kitId}/original-webcheck/${n}/${i}/${path}`); toast.success(data.kind === "replacing" ? "Finding a verified replacement — this takes a minute" : msg); reload(); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const ib = "inline-flex items-center gap-1 rounded-sm px-2 py-0.5 text-[11px] font-bold transition-colors disabled:opacity-50";
  return (
    <div data-testid={`web-issue-${n}-${i}`} className="ml-5 mt-1.5 text-xs text-stone-600">
      <b>{f.field}:</b> your guide says “{f.in_guide}” — now <span className="text-[#7B2D3B] font-semibold">{f.current}</span>
      {f.source && <> · <a href={f.source} target="_blank" rel="noreferrer" className="underline hover:text-black">source</a></>}
      <div className="mt-1 flex flex-wrap items-center gap-2">
        {!a && <button data-testid={`web-apply-${n}-${i}`} disabled={busy} onClick={() => call("apply", "Updated in your original")} className={`${ib} bg-[#2B2A26] text-white hover:bg-black`}>{busy ? <Loader2 size={11} className="animate-spin" /> : <Wand2 size={11} />} {closed ? "Replace with a verified option" : "Apply"}</button>}
        {a?.kind === "working" && <span className="inline-flex items-center gap-1 text-[#9A7B2C]"><Loader2 size={11} className="animate-spin" /> {a.value}…</span>}
        {a?.kind === "edited" && <span data-testid={`web-applied-${n}-${i}`} className="text-emerald-700">Now reads: “{a.value}”</span>}
        {a?.kind === "replaced" && <span data-testid={`web-applied-${n}-${i}`} className="text-emerald-700">Replaced with <b>{a.value}</b>{a.source && <> · <a href={a.source} target="_blank" rel="noreferrer" className="underline">source</a></>}</span>}
        {a?.kind === "failed" && <span className="text-red-700">Couldn't apply: {a.value}</span>}
        {a && a.kind !== "working" && <button data-testid={`web-undo-${n}-${i}`} disabled={busy} onClick={() => call("undo", "Back to your original wording")} className={`${ib} text-stone-500 hover:text-black`}><Undo2 size={11} /> {a.kind === "failed" ? "Clear" : "Undo"}</button>}
      </div>
    </div>
  );
};

const Changes = ({ c }) => !c ? null : (
  <p data-testid="web-check-changes" className="mt-2 text-xs text-stone-600">
    Since the last check{c.previous_at ? ` (${c.previous_at.slice(0, 10)})` : ""}: <b className="text-[#7B2D3B]">{c.new.length} new</b> · <b className="text-emerald-700">{c.resolved.length} fixed</b> · {c.open} still open
    {c.resolved.length > 0 && <span className="block text-stone-500">Fixed: {c.resolved.map((x) => `${x.name} (${x.field})`).join(", ")}</span>}
  </p>
);

export const WebCheck = ({ kit, reload }) => {
  const [busy, setBusy] = useState(false);
  const w = kit.original_webcheck;
  const bulk = w?.bulk;
  const running = w?.status === "running" || bulk?.status === "running" || (w?.venues || []).some((v) => v.issues?.some((i) => i.applied?.kind === "working"));
  const isClosed = isClosedFlag;
  const safeLeft = (w?.venues || []).reduce((n, v) => n + (v.issues || []).filter((f) => !f.applied && !isClosed(f)).length, 0);
  const closedLeft = (w?.venues || []).reduce((n, v) => n + (v.issues || []).filter((f) => !f.applied && isClosed(f)).length, 0);
  const applySafe = async () => {
    setBusy(true);
    try { const { data } = await http.post(`/kits/${kit.id}/original-webcheck-apply-safe`); toast.message(`Applying ${data.total} update(s) one by one — closures are left for you`); reload(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(reload, 8000);
    return () => clearInterval(t);
  }, [running, reload]);
  const start = async () => {
    setBusy(true);
    try { await http.post(`/kits/${kit.id}/original-webcheck`); toast.message("Checking hours, prices and phone numbers against the web"); reload(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const flagged = (w?.venues || []).filter((v) => v.issues?.length).sort((a, b) => a.n - b.n);
  const issues = flagged.reduce((n, v) => n + v.issues.length, 0);
  return (
    <div data-testid="web-check" className="mt-4 rounded-md border border-stone-200 bg-white p-4">
      <div className="flex flex-wrap items-center gap-3">
        <Globe size={15} className="text-[#9A7B2C]" />
        <b className="text-sm text-[#2B2A26]">Web check of hours, prices &amp; phone numbers</b>
        <span data-testid="web-check-status" className="text-xs text-stone-500">
          {w?.status === "running" ? `Checking ${w.done} of ${w.total} places…` : w?.status === "failed" ? `Couldn't finish: ${w.error}` :
            w ? `${w.total} places checked · ${issues ? `${issues} item(s) look out of date` : "nothing out of date"}${w.status === "budget" ? " · stopped: $8 AI budget used up" : ""}` : "Not checked yet"}
        </span>
        <button data-testid="web-check-btn" disabled={busy || running} onClick={start}
          className="ml-auto inline-flex items-center gap-1.5 rounded-sm bg-[#2B2A26] px-3 py-1.5 text-xs font-bold text-white hover:bg-black disabled:opacity-50">
          {(busy || running) && <Loader2 size={12} className="animate-spin" />} {w ? "Check again" : "Check against the web"}
        </button>
      </div>
      <Changes c={w?.changes} />
      {w && w.status !== "running" && (safeLeft > 0 || bulk) && (
        <div className="mt-2 flex flex-wrap items-center gap-3 rounded-sm bg-[#FAF7F0] px-3 py-2">
          {bulk?.status === "running" ? (
            <span data-testid="apply-safe-progress" className="inline-flex items-center gap-1.5 text-xs text-[#2B2A26]"><Loader2 size={12} className="animate-spin text-[#9A7B2C]" /> Applying update {Math.min(bulk.done + bulk.failed + 1, bulk.total)} of {bulk.total}…</span>
          ) : (
            <>
              {bulk && <span data-testid="apply-safe-result" className="text-xs text-stone-600">Last run: {bulk.done} of {bulk.total} applied{bulk.failed ? ` · ${bulk.failed} couldn't be applied` : ""}{bulk.status === "budget" ? " · stopped: $8 AI budget used up" : ""}</span>}
              {safeLeft > 0 && <button data-testid="apply-safe-btn" disabled={busy} onClick={applySafe} className="ml-auto inline-flex items-center gap-1.5 rounded-sm bg-[#7B2D3B] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#5c1f2b] disabled:opacity-50"><Wand2 size={12} /> Apply all {safeLeft} safe fix{safeLeft === 1 ? "" : "es"}</button>}
            </>
          )}
          {closedLeft > 0 && <span className="w-full text-[11px] text-stone-500">{closedLeft} closure(s) are left for you to review below.</span>}
        </div>
      )}
      {flagged.length > 0 && (
        <ul className="mt-3">
          {flagged.map((v) => (
            <li key={v.n} data-testid={`web-flag-${v.n}`} className="border-t border-stone-100 py-2">
              <div className="flex items-center gap-2 text-sm font-semibold text-[#2B2A26]"><AlertTriangle size={13} className="text-amber-600" /> {v.name} <span className="text-xs font-normal text-stone-400">page {v.n + 1}</span></div>
              {v.issues.map((i, k) => <Flag key={k} kitId={kit.id} n={v.n} i={k} f={i} reload={reload} />)}
            </li>
          ))}
        </ul>
      )}
      {w && !running && !flagged.length && w.status === "done" && <p className="mt-2 text-xs text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 size={13} /> Everything checked matches the web.</p>}
      <p className="mt-2 text-xs text-stone-400">Tap Apply to update a line in your original (closed venues are swapped for a verified option). Re-checked automatically every month, with an email of what changed.</p>
    </div>
  );
};
